import * as React from "react";
import { useContext, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { InputChangeEvent } from "@progress/kendo-react-inputs/dist/npm/input/interfaces/InputChangeEvent";
import { toast } from "react-toastify";
import TextInput from "../../../../../../Layout/Inputs/TextInput";
import { FtlRouteViewModel } from "../../../../../ViewModel/FtlRouteViewModel";
import SharedDialog, { SharedDialogControls } from "../../../../../../Layout/Dialog/SharedDialog";
import { EditProjectContext } from "../../../../EditProjectContext";
import { getGsdbAddErrorMessage } from "./getGsdbAddErrorMessage";
import generateRoutingJson from "../../../../GenerateRoutingJson";
import { InputButtonGroup } from "../../../../../../Layout/Inputs/InputButtonGroup";
import { getGsdbInfoRequest } from "./GetGsdbInfoRequest";
import { setRoutingTable } from "./SetRoutingTable";
import { setSpdrsOnThisRoute } from "../../../../SetSpdrsOnThisRoute";
import { setPdsrsOnThisRoute } from "../../../../SetPdsrsOnThisRoute";
import { FtlRoutingViewModel } from "../../../../../ViewModel/FtlRoutingViewModel";
import { DraggableElement } from "../../../../../ViewModel/DraggableElement";
import { RoutingType } from "../../../../../ViewModel/RoutingType";

function isRoutingAlreadyAdded(
  route: FtlRouteViewModel,
  draggableElement: DraggableElement
): boolean {
  let added = false;
  const element: FtlRoutingViewModel | undefined = route.RoutingTable?.filter(
    (item) => item.Gsdb === draggableElement.Gsdb
  )[0];

  const routingAlreadyExists = element !== undefined;
  const isPlantOrEdc =
    draggableElement.RoutingType === RoutingType.Plant ||
    draggableElement.RoutingType === RoutingType.EDC;
  if (routingAlreadyExists && isPlantOrEdc) {
    const dockIsAlreadyAdded =
      draggableElement.DockList.filter((item) => item.Dock === element?.Dock).length > 0;
    if (dockIsAlreadyAdded) {
      added = true;
    }
  }
  return added;
}

type Props = {
  route: FtlRouteViewModel;
  isReadonly: boolean;
};

function AddGsdb({ route, isReadonly }: Props) {
  const { project, setProject } = useContext(EditProjectContext);
  const handleGsdbChange = (event: InputChangeEvent) => {
    setGsdb(event.target.value as string);
  };
  const [gsdb, setGsdb] = React.useState("");
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [successResponse, setSuccessResponse] = useState<any>();

  const handleDialogOpen = () => {
    setIsOpen(true);
  };

  const handleDialogClose = () => {
    setIsOpen(false);
  };

  async function addGsdbClick() {
    try {
      const data = await getGsdbInfoRequest(gsdb);
      addDraggableElement(data);
    } catch (error: any) {
      toast.error(error.message);
    }
  }

  const addDraggableElement = (data: Array<DraggableElement>) => {
    if (data.length > 1) {
      setSuccessResponse(data);
      handleDialogOpen();
    } else {
      addUniqueGSDB(data[0]);
    }
  };

  const addUniqueGSDB = (draggableElement: DraggableElement) => {
    // eslint-disable-next-line no-param-reassign
    draggableElement.RoutingInfo.Modified = true;
    const errorMessage = getGsdbAddErrorMessage(draggableElement, route);
    if (errorMessage) {
      toast(errorMessage, {
        type: "error",
      });
      return;
    }
    const added = isRoutingAlreadyAdded(route, draggableElement);

    if (draggableElement.RoutingType === RoutingType.ODC) {
      route.SpdrsOnThisRoute?.forEach((item) => {
        item.ODCs?.push({ OdcGsdb: draggableElement.Gsdb });
      });

      setSpdrsOnThisRoute(route.RouteId, route.SpdrsOnThisRoute || [], {
        project,
        setProject,
      });
    }

    if (draggableElement.RoutingType === RoutingType.ODC) {
      route.PdsrsOnThisRoute?.forEach((item) => {
        item.ODCs?.push({ OdcGsdb: draggableElement.Gsdb });
      });

      setPdsrsOnThisRoute(route.RouteId, route.PdsrsOnThisRoute || [], {
        project,
        setProject,
      });
    }

    if (!added) {
      const routingTable = [
        ...(route.RoutingTable || []),
        ...generateRoutingJson(route, [draggableElement]),
      ];

      setRoutingTable(route.RouteId, routingTable || [], {
        project,
        setProject,
      });
    }

    toast(`GSDB ${gsdb} was added successfully`, {
      type: "success",
    });
    setGsdb("");
  };

  const addUniqueGSDBByType = (routingType: number) => {
    const givenLocation = successResponse.filter(
      (location: FtlRoutingViewModel) => location.RoutingType === routingType
    );
    addUniqueGSDB(givenLocation[0]);
    handleDialogClose();
  };

  return (
    <div style={{ width: "100%" }}>
      <InputButtonGroup>
        <TextInput
          label="GSDB"
          onChange={handleGsdbChange}
          name="gsdb"
          value={gsdb}
          disabled={isReadonly}
        />

        {isReadonly ? (
          <Button disabled style={{ height: "30px", marginTop: "20px" }}>
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        ) : (
          <Button onClick={() => addGsdbClick()} style={{ height: "30px", marginTop: "20px" }}>
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        )}
      </InputButtonGroup>
      <SharedDialog
        title="Choose you Location Type"
        subtitle="Is your Location a EPC, an ODC, a Plant or a Supplier?"
        isOpen={isOpen}
        handleClose={handleDialogClose}
        width="sm"
      >
        <SharedDialogControls>
          <button
            onClick={handleDialogClose}
            type="button"
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
          >
            Cancel
          </button>
          <button
            onClick={() => addUniqueGSDBByType(RoutingType.EPC)}
            type="button"
            className={classNameForTypesDialogButton}
          >
            EPC
          </button>
          <button
            onClick={() => addUniqueGSDBByType(RoutingType.ODC)}
            type="button"
            className={classNameForTypesDialogButton}
          >
            ODC
          </button>
          <button
            onClick={() => addUniqueGSDBByType(RoutingType.Plant)}
            type="submit"
            className={classNameForTypesDialogButton}
          >
            Plant
          </button>
          <button
            onClick={() => addUniqueGSDBByType(RoutingType.Supplier)}
            type="button"
            className={classNameForTypesDialogButton}
          >
            Supplier
          </button>
        </SharedDialogControls>
      </SharedDialog>
    </div>
  );
}

export default AddGsdb;

const classNameForTypesDialogButton =
  "k-button k-button-md k-rounded-md k-button-solid k-button-solid-error";
